import type { IconName } from '@zapier/design-system';

//////////////////////////////////////////////////////////////////////
// Link typings
//////////////////////////////////////////////////////////////////////

export type UniversalNavLinkType = {
  href: string | null;
  icon?: IconName;
  isExternal?: boolean;
  text: string;
  description?: string;
  submenu?: Array<UniversalNavLinkType>;
};


//////////////////////////////////////////////////////////////////////
// Topbar links
//////////////////////////////////////////////////////////////////////

export const whyZapierLink: UniversalNavLinkType = {
  href: null,
  text: 'Why Zapier?',
  submenu: [
    {
      href: '/how-it-works',
      text: 'How Zapier works',
    },
    {
      href: '/demo',
      text: 'Product tour',
    },
    {
      href: '/customers',
      text: 'Customer stories',
    },
    {
      href: '/security',
      text: 'Security',
    },
  ],
};

export const exploreLink: UniversalNavLinkType = {
  href: null,
  text: 'Explore',
  submenu: [
    {
      href: '/explore',
      text: 'Popular ways to use Zapier',
    },
    {
      href: '/apps',
      text: 'Apps that work with Zapier',
    },
    {
      href: '/roles',
      text: 'Explore Zapier by job role',
    },
  ],
};

export const resourcesLink: UniversalNavLinkType = {
  href: null,
  text: 'Resources',
  submenu: [
    {
      href: '/blog',
      text: 'Blog',
      description: 'Read the Zapier blog for tips on productivity, automation, and growing your business',
    },
    {
      href: '/experts',
      text: 'Experts',
      description: 'Hire a Zapier Expert to help you improve processes and automate workflows.',
    },
    {
      href: '/help',
      text: 'Help center',
      description: 'Get help with Zapier from our tutorials, FAQs, and troubleshooting articles.',
    },
    {
      href: 'https://community.zapier.com',
      text: 'Community',
      description: 'Ask questions, share your knowledge, and get inspired by other Zapier users.',
    },
  ],
};

export const teamsLink: UniversalNavLinkType = {
  href: null,
  text: 'Teams & Companies',
  submenu: [
    {
      href: '/teams',
      text: 'Zapier for Teams',
      description: 'Share and collaborate on work with your team in Zapier.',
    },
    {
      href: '/companies',
      text: 'Zapier for Companies',
      description: 'Manage multiple teams with advanced administrative controls in Zapier.',
    },
  ],
};

export const pricingLink: UniversalNavLinkType = {
  href: '/pricing',
  text: 'Pricing',
};


//////////////////////////////////////////////////////////////////////
// Sidebar links
//////////////////////////////////////////////////////////////////////

export const dashboardLink: UniversalNavLinkType = {
  href: '/app/dashboard',
  icon: 'navDashboard' as 'navDashboard',
  text: 'Dashboard',
};

export const zapsLink: UniversalNavLinkType = {
  href: '/app/zaps',
  icon: 'miscBolt' as 'miscBolt',
  text: 'Zaps',
};

export const myAppsLink: UniversalNavLinkType = {
  href: '/app/connections',
  icon: 'navApps' as 'navApps',
  text: 'My Apps',
};

export const historyLink: UniversalNavLinkType = {
  href: '/app/history/usage',
  icon: 'miscClock' as 'miscClock',
  text: 'Task History',
};

export const exploreLinkInternal: UniversalNavLinkType = {
  href: '/explore',
  icon: 'navExplore' as 'navExplore',
  text: 'Explore',
};

export const helpLink: UniversalNavLinkType = {
  href: '/help',
  icon: 'navHelp' as 'navHelp',
  isExternal: true,
  text: 'Get Help',
};

export const usageLink: UniversalNavLinkType = {
  href: '/app/settings/billing',
  icon: 'miscWallet' as 'miscWallet',
  text: 'Usage',
};


//////////////////////////////////////////////////////////////////////
// Account menu links
//////////////////////////////////////////////////////////////////////

export const moveZapsLink: UniversalNavLinkType = {
  href: '/app/move-zaps/',
  icon: 'arrowBigRight' as 'arrowBigRight',
  text: 'Move Zaps',
};

export const settingsLink: UniversalNavLinkType = {
  href: '/app/settings/profile',
  icon: 'navCog' as 'navCog',
  text: 'Settings',
};

export const logOutLink: UniversalNavLinkType = {
  href: '/logout',
  icon: 'arrowExit' as 'arrowExit',
  text: 'Log Out',
};


//////////////////////////////////////////////////////////////////////
// Groups of links
//////////////////////////////////////////////////////////////////////

export const UniversalSidebarLinks: Array<UniversalNavLinkType> = [
  dashboardLink,
  zapsLink,
  myAppsLink,
  historyLink,
  exploreLinkInternal,
  helpLink,
];

export const UniversalTopbarLinks: Array<UniversalNavLinkType> = [
  whyZapierLink,
  exploreLink,
  resourcesLink,
  teamsLink,
  pricingLink,
];

export const AccountsMenuLinks: Array<UniversalNavLinkType> = [
  // `moveZapsLink` conditionally rendered.
  settingsLink,
  logOutLink,
];
